'use client';

import React, { FC, useState } from 'react';

import { Entry } from 'contentful';

import { Document } from '@contentful/rich-text-types';

import {
  AccordionSection,
  CardsRow,
  DecorativeDivider,
  DynamicComponent,
  HeaderText,
  NewsletterBlock,
  ProductDetailsHeader,
  ProductSubFAQ,
  RichText,
  NotFound,
  YotpoReviewsWidget,
  TrustedShopReviews,
} from '@/components';

import { BackToTop } from '@/components/common/BackToTop';

import { HeaderSize } from '@/components/ui/HeaderText';
import { Seo } from '@/components/ui/Seo';

import { ProductContextProvider } from '@/context/ProductContext';

import {
  TypeDecorativeDividerFields,
  TypeImage,
  TypeNewsletterBlockFields,
  TypeProductPage,
  TypeTextBlock,
} from '@/core/generated-types';

import useFeature from '@/hooks/useFeature';

import { ShopifyProduct } from '@/lib/shopify';

import {
  ExtendedProductFields,
  buildCanonicalUrl,
} from '@/core/contentfulConfig';
import {
  InventoryStatusCopy,
  ProductVariantCopy,
  ProductPageGeneralCopy,
} from '@/lib/contentful';
import ProductSchema from '@/components/common/Schema/ProductSchema';
import {
  shouldUseYotpoReviews,
  shouldUseTrustedShopsReviews,
} from '@/utils/helpers';
import { getProductPageTranslation } from '@/utils/getProductPageTranslation';
import { ReviewTotals } from '@/lib/yotpo';
import ReviewsSchema from '@/components/common/Schema/ReviewsSchema';
import { CarouselContextProvider } from '@/context/CarouselContext';
import generateTranslationsObject from '@/utils/translations/generateTranslationObject';
import { TypeSubscriptionPlanInstallmentsResponse } from '@/lib/api/subscriptionManager/types';
import { ErrorDirectiveProvider } from '@/context/ErrorDirectiveContext';
import { TypeTariffSettings } from '@/components/common/types';
import { FeatureFlagValues } from '@/core/featureFlags';

export interface ProductPageLayoutProps extends TypeProductPage {
  shopifyProduct: ShopifyProduct | null;
  shopifyAdditionalProducts: ShopifyProduct[];
  shopifyAlternativeProduct?: ShopifyProduct;
  inventoryStatusCopy: InventoryStatusCopy | null;
  productVariantsCopy: ProductVariantCopy | null;
  productPageGeneralCopy: ProductPageGeneralCopy | null;
  subscriptionPlanInstallments: TypeSubscriptionPlanInstallmentsResponse;
  yotpoReviews?: ReviewTotals;
  tariffSettings?: TypeTariffSettings;
}

export const ProductPageLayout: FC<ProductPageLayoutProps> = ({
  shopifyProduct,
  shopifyAdditionalProducts,
  shopifyAlternativeProduct,
  inventoryStatusCopy,
  productVariantsCopy,
  productPageGeneralCopy,
  subscriptionPlanInstallments,
  yotpoReviews,
  tariffSettings,
  ...page
}) => {
  const [carouselImagePosition, setCarouselImagePosition] = useState(null);

  const {
    fields: {
      product,
      additionalProducts,
      alternativeProduct,
      productDescription,
      lookInside,
      marketingComponents,
      productFaq,
      productSubFaq,
      enableYotpoReviewsSummary,
      enableYotpoReviewsWidget,
      enableTrustedShopsReviewsSummary,
      enableTrustedShopsReviewsWidget,
      newsletterSignup,
      lookInsideDivider,
      relatedProducts,
      lookInsideLeadIn,
      featureFlagName,
      tabsTranslations,
      shipmentList,
      enableStickyCarousel,
      errorDirectives,
      alternativeTariffText,
      alternativeTariffTooltip,
    },
  } = page;

  const updatedTariffSettings = {
    exclusionList: tariffSettings?.exclusionList || [],
    translations: {
      tariffMessage: alternativeTariffText
        ? alternativeTariffText
        : tariffSettings?.translations?.tariffMessage || '',
      tariffTooltip: alternativeTariffTooltip
        ? alternativeTariffTooltip
        : tariffSettings?.translations?.tariffTooltip || '',
    },
  };

  const metadataFields = page.fields?.pageMetafields?.fields;

  const showReviewsTitle =
    (shouldUseYotpoReviews() && enableYotpoReviewsWidget) ||
    (shouldUseTrustedShopsReviews() && enableTrustedShopsReviewsWidget);

  const isFlagEnabled = useFeature(featureFlagName as keyof FeatureFlagValues);

  const isPageEnabled = isFlagEnabled ?? true;

  const t = generateTranslationsObject(tabsTranslations?.fields);

  const shouldRenderBelowFoldDetailsSection = productDescription && productFaq;

  if (!isPageEnabled) {
    return <NotFound />;
  }

  return (
    <ProductContextProvider
      value={{
        enableYotpoReviewsSummary,
        enableTrustedShopsReviewsSummary,
        product,
        shopifyProduct,
        inventoryStatusCopy,
        productVariantsCopy,
      }}
    >
      <ErrorDirectiveProvider errorDirectives={errorDirectives || []}>
        <CarouselContextProvider
          value={{ carouselImagePosition, setCarouselImagePosition }}
        >
          <Seo
            title={metadataFields?.pageTitle}
            description={metadataFields?.pageDescription}
            nofollow={metadataFields?.noFollow ?? true}
            noindex={metadataFields?.noIndex ?? true}
            canonical={buildCanonicalUrl(page)}
            openGraph={{
              url: buildCanonicalUrl(page),
            }}
          />
          <div
            className="md:pt-12"
            itemScope
            itemType="https://schema.org/Product"
            key={product.sys?.id}
          >
            <ProductSchema product={product as Entry<ExtendedProductFields>} />

            {shouldUseYotpoReviews() && enableYotpoReviewsWidget && (
              <ReviewsSchema
                totals={yotpoReviews}
                product={product as Entry<ExtendedProductFields>}
              />
            )}

            <ProductDetailsHeader
              shopifyProduct={shopifyProduct}
              shopifyAdditionalProducts={shopifyAdditionalProducts}
              product={product}
              additionalProducts={additionalProducts}
              alternativeProduct={alternativeProduct}
              shopifyAlternativeProduct={shopifyAlternativeProduct}
              t={t}
              tariffSettings={updatedTariffSettings}
              shipmentList={shipmentList}
              enableStickyCarousel={enableStickyCarousel}
              subscriptionPlanInstallments={subscriptionPlanInstallments}
            />

            {shouldRenderBelowFoldDetailsSection && (
              <div className="max-auto mx-auto px-6 md:container lg:max-w-[1200px]">
                <div className="border-t-[1px] sm:mt-8"></div>
                <div className="mb-8 flex flex-col py-8 md:flex-row md:py-12">
                  <div className="md:w-7/12">
                    {productDescription && (
                      <RichText content={productDescription as Document} />
                    )}
                  </div>
                  <div className="md:w-1/12" />
                  <div className="flex flex-col pt-4 md:w-5/12 md:pt-16">
                    {productFaq && <AccordionSection {...productFaq?.fields} />}

                    {productSubFaq && (
                      <ProductSubFAQ
                        entries={productSubFaq as TypeImage[] | TypeTextBlock[]}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}

            {lookInside && (
              <div className="relative">
                {lookInsideDivider && (
                  <div className="absolute -top-5 z-0 h-24 w-full">
                    <DecorativeDivider
                      {...(lookInsideDivider.fields as unknown as TypeDecorativeDividerFields)}
                    />
                  </div>
                )}

                {lookInsideLeadIn && (
                  <DynamicComponent component={lookInsideLeadIn} />
                )}

                {lookInside.map((component, i) => (
                  <DynamicComponent
                    key={`${component.sys.id}-${i}`}
                    component={component}
                  />
                ))}
              </div>
            )}

            <div className="mx-auto">
              {marketingComponents &&
                marketingComponents.map((component, i) => (
                  <DynamicComponent
                    key={`${component.sys.id}-${i}`}
                    component={component}
                  />
                ))}
            </div>
            <div className="mx-auto max-w-[900px]">
              {showReviewsTitle && (
                <HeaderText
                  size={HeaderSize.H1}
                  text={getProductPageTranslation(
                    productPageGeneralCopy,
                    'Reviews',
                  )}
                  align="left"
                  classes="border-b-[1px] border-charcoal-opacity-20 mb-4 mt-4 container mx-auto"
                />
              )}

              {shouldUseYotpoReviews() && enableYotpoReviewsWidget && (
                <YotpoReviewsWidget product={product} />
              )}

              {shouldUseTrustedShopsReviews() &&
                enableTrustedShopsReviewsWidget && (
                  <div className="container mx-auto mb-24 px-6">
                    <TrustedShopReviews
                      shopifyProduct={shopifyProduct}
                      widgetType="reviews"
                    />
                  </div>
                )}
            </div>

            {relatedProducts && (
              <div className="mx-auto">
                <CardsRow {...relatedProducts.fields} />
              </div>
            )}

            {newsletterSignup && (
              <NewsletterBlock
                {...(newsletterSignup.fields as unknown as TypeNewsletterBlockFields)}
              />
            )}
            <BackToTop />
          </div>
        </CarouselContextProvider>
      </ErrorDirectiveProvider>
    </ProductContextProvider>
  );
};
